import type { ApiResult, ProbeResponse } from '@warehouselogix/shared';

/**
 * Offline engine — the floor PWA's network policy, PURE and injectable.
 *
 * This reconciles two proven designs:
 *
 *  - the JJB-WMS monolith's netShim (jjb_wms_sl.js 103-757): every request is
 *    sorted into NEVER SENT (hold, send when signal returns), READ (retry
 *    freely) or UNKNOWN (bytes went out, nothing came back — NEVER retried
 *    blindly; the key is journalled and the server is asked what happened);
 *
 *  - WorkLogix's kiosk queue/syncEngine: the durable IndexedDB journal that
 *    survives reloads and drains with idempotency keys.
 *
 * Differences from the monolith, on purpose: no XHR/fetch monkey-patching —
 * tools call read()/write() explicitly; and the "send again anyway?" confirm()
 * becomes a structured STILL_SAVING failure the UI renders (a library must not
 * own dialogs).
 *
 * Everything environmental (fetch, IndexedDB, navigator.onLine, timers,
 * crypto) enters through {@link OfflineEnv}, which is what makes the whole
 * policy unit-testable with fakes and keeps this file free of DOM types.
 */

// ── Ports ────────────────────────────────────────────────────────────────────

/** One transport attempt, no policy. */
export type TransportOutcome =
  | { kind: 'answered'; status: number; body: unknown }
  /** Bytes may have left; nothing came back. Fate unknown. */
  | { kind: 'unknown' }
  /** The send itself failed before anything left the device. */
  | { kind: 'failed' };

export interface NetTransport {
  post(path: string, body: unknown, timeoutMs: number): Promise<TransportOutcome>;
}

/** A journalled in-doubt write (survives reloads; settled by probing). */
export interface JournalEntry {
  ik: string;
  tool: string;
  action: string;
  /** Epoch ms the write was attempted. */
  ts: number;
}

export interface JournalStore {
  add(entry: JournalEntry): Promise<void>;
  drop(ik: string): Promise<void>;
  list(): Promise<JournalEntry[]>;
}

export interface OfflineEnv {
  transport: NetTransport;
  journal: JournalStore;
  isOnline(): boolean;
  /** Resolves when the device believes it has a network again. */
  whenOnline(): Promise<void>;
  delay(ms: number): Promise<void>;
  now(): number;
  /** Mint an idempotency key (crypto.randomUUID in the browser). */
  mintKey(): string;
}

export interface OfflineOptions {
  /** Per-attempt send timeout (netShim SEND_MS). */
  sendTimeoutMs: number;
  /** Probe timeout (probes are cheap and read-only). */
  probeTimeoutMs: number;
  /** How long a never-sent request is held for signal (netShim HOLD_MS). */
  holdMs: number;
  /** Blind retries for reads (netShim readRetries). */
  readRetries: number;
  /** Retries when send() itself failed — nothing left the device. */
  failedRetries: number;
  /** Journal entries older than this are discarded on settle (24 h). */
  journalMaxAgeMs: number;
  /** How long ok/warn settle notes linger before auto-clearing. */
  noteOkMs: number;
  noteWarnMs: number;
}

export const OFFLINE_DEFAULTS: OfflineOptions = {
  sendTimeoutMs: 45_000,
  probeTimeoutMs: 20_000,
  holdMs: 120_000,
  readRetries: 3,
  failedRetries: 2,
  journalMaxAgeMs: 86_400_000,
  noteOkMs: 6_000,
  noteWarnMs: 8_000,
};

// ── Connection state (drives the status pill) ────────────────────────────────

export type PillKind = 'ok' | 'busy' | 'warn' | 'bad';

export interface PillState {
  kind: PillKind;
  text: string;
}

export interface NetState {
  /** Writes currently held for signal. */
  holding: number;
  /** Writes currently in flight. */
  saving: number;
  /** Sticky note from reconcile/settle; overrides the derived pill. */
  note: PillState | null;
}

/** Stable error codes surfaced by the engine (message text is operator-facing). */
export const NET_CODES = {
  stillSaving: 'STILL_SAVING',
  holdTimeout: 'HOLD_TIMEOUT',
  neverSent: 'NEVER_SENT',
  httpError: 'HTTP_ERROR',
  badReply: 'BAD_REPLY',
  readLost: 'READ_LOST',
  probeUnreachable: 'PROBE_UNREACHABLE',
  inflight: 'INFLIGHT',
} as const;

function failResult<T>(code: string, message: string, retryable: boolean): ApiResult<T> {
  return { ok: false, error: { code, message, retryable } };
}

function isApiResult(x: unknown): x is ApiResult<unknown> {
  return typeof x === 'object' && x !== null && typeof (x as { ok?: unknown }).ok === 'boolean';
}

function fingerprint(tool: string, action: string, payload: unknown): string {
  try {
    return `${tool}|${action}|${JSON.stringify(payload ?? null)}`;
  } catch {
    return `${tool}|${action}`;
  }
}

// ── The engine ───────────────────────────────────────────────────────────────

export class OfflineEngine {
  private readonly env: OfflineEnv;
  private readonly opts: OfflineOptions;
  private readonly listeners = new Set<() => void>();
  /**
   * Writes in flight, keyed by WHAT they are rather than by which request
   * carried them: two taps on Save produce identical payloads, so an identical
   * payload already in flight IS the same operation (netShim `inflight`).
   */
  private readonly inflight = new Map<string, { ik: string; at: number }>();
  private state: NetState = { holding: 0, saving: 0, note: null };

  constructor(env: OfflineEnv, opts: Partial<OfflineOptions> = {}) {
    this.env = env;
    this.opts = { ...OFFLINE_DEFAULTS, ...opts };
  }

  getState(): NetState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** The pill the UI renders, derived netShim-style (refreshPill). */
  pill(): PillState | null {
    if (this.state.note !== null) {
      return this.state.note;
    }
    if (this.state.holding > 0) {
      return {
        kind: 'warn',
        text:
          this.state.holding === 1
            ? 'No signal — holding your last action'
            : `No signal — holding ${this.state.holding} actions`,
      };
    }
    if (this.state.saving > 0) {
      return {
        kind: 'busy',
        text: this.state.saving === 1 ? 'Saving…' : `Saving ${this.state.saving} things…`,
      };
    }
    return null;
  }

  private setState(patch: Partial<NetState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) {
      l();
    }
  }

  private setNote(note: PillState | null, autoClearMs?: number): void {
    this.setState({ note });
    if (note !== null && autoClearMs !== undefined) {
      void this.env.delay(autoClearMs).then(() => {
        if (this.state.note === note) {
          this.setState({ note: null });
        }
      });
    }
  }

  // ── Reads: no side effects, retry freely ──────────────────────────────────

  async read<T>(tool: string, action: string, payload: unknown): Promise<ApiResult<T>> {
    const body = { action, payload };
    let tries = this.opts.readRetries;
    for (;;) {
      const held = await this.holdForSignal();
      if (!held) {
        return failResult(
          NET_CODES.holdTimeout,
          `Still no signal after ${Math.max(1, Math.round(this.opts.holdMs / 1000))} seconds. ` +
            'Nothing was sent, so nothing has changed. Move somewhere with coverage and do it again.',
          true
        );
      }
      const outcome = await this.env.transport.post(
        `/floor/tools/${tool}`,
        body,
        this.opts.sendTimeoutMs
      );
      if (outcome.kind === 'answered') {
        if (outcome.status >= 200 && outcome.status < 300) {
          return this.parse<T>(outcome.body);
        }
        if (outcome.status >= 500 && tries > 0) {
          tries--;
          await this.env.delay(800);
          continue;
        }
        return failResult(
          NET_CODES.httpError,
          `The server returned an error (${outcome.status}). Nothing was posted. Try again.`,
          true
        );
      }
      // failed or unknown: reads are safe to retry blindly.
      if (tries > 0) {
        tries--;
        await this.env.delay(800);
        continue;
      }
      return failResult(
        NET_CODES.readLost,
        'Lost the connection to the server. Nothing was changed - try that again.',
        true
      );
    }
  }

  // ── Writes: journal + hold + probe-then-resend ────────────────────────────

  async write<T>(tool: string, action: string, payload: unknown): Promise<ApiResult<T>> {
    const fp = fingerprint(tool, action, payload);
    const busy = this.inflight.get(fp);
    if (busy !== undefined) {
      const secs = Math.max(1, Math.round((this.env.now() - busy.at) / 1000));
      return failResult(
        NET_CODES.stillSaving,
        `Still saving (${secs} second${secs === 1 ? '' : 's'}) — give it a moment.`,
        false
      );
    }

    const ik = this.env.mintKey();
    this.inflight.set(fp, { ik, at: this.env.now() });
    this.setState({ saving: this.state.saving + 1 });

    try {
      // Durable BEFORE any network: a write the device accepted is never lost
      // to a dropped connection or a reload (WorkLogix queue discipline).
      await this.journalAddSafe({ ik, tool, action, ts: this.env.now() });
      return await this.send<T>(tool, { action, payload, _ik: ik }, ik, true);
    } finally {
      this.inflight.delete(fp);
      this.setState({ saving: Math.max(0, this.state.saving - 1) });
    }
  }

  private async send<T>(
    tool: string,
    envelope: { action: string; payload: unknown; _ik: string },
    ik: string,
    mayResend: boolean
  ): Promise<ApiResult<T>> {
    let failedLeft = this.opts.failedRetries;
    for (;;) {
      const held = await this.holdForSignal();
      if (!held) {
        // NEVER SENT and the operator has waited long enough.
        await this.journalDropSafe(ik);
        return failResult(
          NET_CODES.holdTimeout,
          `Still no signal after ${Math.max(1, Math.round(this.opts.holdMs / 1000))} seconds. ` +
            'Nothing was sent, so nothing has changed. Move somewhere with coverage and do it again.',
          true
        );
      }

      const outcome = await this.env.transport.post(
        `/floor/tools/${tool}`,
        envelope,
        this.opts.sendTimeoutMs
      );

      if (outcome.kind === 'answered') {
        if (outcome.status >= 200 && outcome.status < 300) {
          await this.journalDropSafe(ik);
          return this.parse<T>(outcome.body);
        }
        // An error reply IS an answer: the server refused, nothing posted.
        // When the refusal is the API's own ApiResult (401 "Sign in to do
        // that.", 409 in flight, 400 bad request) the operator hears those
        // words, not a status code.
        await this.journalDropSafe(ik);
        if (outcome.status < 500 && isApiResult(outcome.body) && !outcome.body.ok) {
          return outcome.body;
        }
        return failResult(
          NET_CODES.httpError,
          `The server returned an error (${outcome.status}). Nothing was posted. Try again.`,
          true
        );
      }

      if (outcome.kind === 'failed') {
        // send() itself threw - nothing left the device. Bounded retry.
        if (failedLeft > 0) {
          failedLeft--;
          await this.env.delay(400);
          continue;
        }
        await this.journalDropSafe(ik);
        return failResult(
          NET_CODES.neverSent,
          'Could not send. Nothing was sent, so nothing has changed. Try again.',
          true
        );
      }

      // UNKNOWN: bytes went out and nothing came back. NEVER retried blindly.
      return this.reconcile<T>(tool, envelope, ik, mayResend);
    }
  }

  /** Ask the server what happened to the key, then act on the answer. */
  private async reconcile<T>(
    tool: string,
    envelope: { action: string; payload: unknown; _ik: string },
    ik: string,
    mayResend: boolean
  ): Promise<ApiResult<T>> {
    this.setNote({ kind: 'warn', text: 'Connection dropped — checking what landed' });
    await this.env.whenOnline();
    const probe = await this.probe(ik);
    this.setNote(null);

    if (probe === null) {
      // Cannot reach the server to ask. The journal entry STAYS so a reload
      // settles it (settle()).
      return failResult(
        NET_CODES.probeUnreachable,
        'Lost the connection and could not reach the server to check whether your last action ' +
          'went through. Do NOT repeat it until you have looked at the bin.',
        false
      );
    }
    if (probe.state === 'done') {
      // It landed. Hand back the reply it produced the first time and the
      // operator never knows.
      await this.journalDropSafe(ik);
      return this.parse<T>(probe.response);
    }
    if (probe.state === 'none') {
      // Never started - safe to resend, SAME key (once).
      if (mayResend) {
        return this.send<T>(tool, envelope, ik, false);
      }
      await this.journalDropSafe(ik);
      return failResult(
        NET_CODES.readLost,
        'Lost the connection to the server. Nothing was changed - try that again.',
        true
      );
    }
    // inflight
    await this.journalDropSafe(ik);
    return failResult(
      NET_CODES.inflight,
      'The connection dropped while the server was still working on that one, so it may or may ' +
        'not have posted. Check the bin before doing it again.',
      false
    );
  }

  /** Probe an idempotency key. Read-only; bypasses all policy. */
  async probe(ik: string): Promise<ProbeResponse | null> {
    const outcome = await this.env.transport.post(
      '/floor/probe',
      { _ik: ik },
      this.opts.probeTimeoutMs
    );
    if (outcome.kind !== 'answered' || outcome.status < 200 || outcome.status >= 300) {
      return null;
    }
    const body = outcome.body as ProbeResponse | null;
    if (body === null || typeof body !== 'object' || typeof body.state !== 'string') {
      return null;
    }
    return body;
  }

  // ── Settle: on load, resolve anything a previous page left hanging ─────────

  async settle(): Promise<{ landed: number; nothing: number; unknown: number }> {
    const pending = await this.env.journal.list();
    const cutoff = this.env.now() - this.opts.journalMaxAgeMs;
    const stale = pending.filter((e) => e.ts >= cutoff);
    // Ancient entries are noise, not evidence.
    for (const e of pending) {
      if (e.ts < cutoff) {
        await this.journalDropSafe(e.ik);
      }
    }
    if (stale.length === 0) {
      return { landed: 0, nothing: 0, unknown: 0 };
    }

    this.setNote({ kind: 'warn', text: 'Checking an action from before…' });
    let landed = 0;
    let nothing = 0;
    let unknown = 0;
    for (const e of stale) {
      const r = await this.probe(e.ik);
      if (r === null || r.state === 'inflight') {
        unknown++;
      } else if (r.state === 'done') {
        landed++;
      } else {
        nothing++;
      }
      await this.journalDropSafe(e.ik);
    }

    if (unknown > 0) {
      this.setNote({
        kind: 'bad',
        text: `${unknown} earlier action(s) may not have finished — check the bin`,
      });
    } else if (landed > 0) {
      this.setNote({ kind: 'ok', text: 'Your last action did go through' }, this.opts.noteOkMs);
    } else if (nothing > 0) {
      this.setNote(
        { kind: 'warn', text: 'Your last action never posted — redo it' },
        this.opts.noteWarnMs
      );
    } else {
      this.setNote(null);
    }
    return { landed, nothing, unknown };
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /**
   * NEVER SENT: hold the request while offline, up to holdMs. The tool stays
   * in its busy state and the operator's tap simply completes when they walk
   * back into coverage. Returns false when the hold timed out.
   */
  private async holdForSignal(): Promise<boolean> {
    if (this.env.isOnline()) {
      return true;
    }
    this.setState({ holding: this.state.holding + 1 });
    try {
      let timedOut = false;
      await Promise.race([
        this.env.whenOnline(),
        this.env.delay(this.opts.holdMs).then(() => {
          timedOut = true;
        }),
      ]);
      return !(timedOut && !this.env.isOnline());
    } finally {
      this.setState({ holding: Math.max(0, this.state.holding - 1) });
    }
  }

  private parse<T>(body: unknown): ApiResult<T> {
    if (isApiResult(body)) {
      return body as ApiResult<T>;
    }
    return failResult(NET_CODES.badReply, 'Unreadable reply from the server. Try again.', true);
  }

  private async journalAddSafe(entry: JournalEntry): Promise<void> {
    try {
      await this.env.journal.add(entry);
    } catch {
      // Private mode / quota: the policy still works, just not across reloads.
    }
  }

  private async journalDropSafe(ik: string): Promise<void> {
    try {
      await this.env.journal.drop(ik);
    } catch {
      // Same as add: best effort.
    }
  }
}
